// src/components/Modals/ExtendWarrantyModal.js
import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  Box, Button, CircularProgress, Container, Card, CardContent,
  Dialog, AppBar, Toolbar, IconButton, Slide, Stack,
  TextField, Typography, InputAdornment,
} from '@mui/material';
import CloseIcon          from '@mui/icons-material/Close';
import CalendarMonthIcon  from '@mui/icons-material/CalendarMonth';
import ReceiptLongIcon    from '@mui/icons-material/ReceiptLong';
import CurrencyRupeeIcon  from '@mui/icons-material/CurrencyRupee';
import ChatIcon           from '@mui/icons-material/Chat';
import InfoOutlinedIcon   from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon   from '@mui/icons-material/WarningAmber';
import CalendarTodayIcon  from '@mui/icons-material/CalendarToday';
import ArrowForwardIcon   from '@mui/icons-material/ArrowForward';

import { assetService } from "../../services/assetService";

import {
  isWarrantyExtensionBlocked,
  validateWarrantyExtensionForm,
  buildWarrantyExtensionPayload,
  getWarrantyMinDate,
} from "../../utils/assetUtils";

// ─────────────────────────────────────────────────────────────────────────────
// SLIDE TRANSITION
// ─────────────────────────────────────────────────────────────────────────────

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ─────────────────────────────────────────────────────────────────────────────
// STYLED COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

const SectionCard = styled(Card)(() => ({
  borderRadius: 8,
  border: '1px solid #e2e8f0',
}));

const SectionTitle = styled(Typography)(() => ({
  fontSize: '0.9375rem',
  fontWeight: 700,
  color: '#1e293b',
  marginBottom: '1.25rem',
  paddingBottom: '0.75rem',
  borderBottom: '1px solid #e5e7eb',
}));

const FormRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  gap: '1rem',
  marginBottom: '1rem',
  '&:last-child': { marginBottom: 0 },
  [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' },
}));

const FormGroup = styled(Box, {
  shouldForwardProp: (p) => p !== 'hasError' && p !== 'span',
})(({ hasError, span = 2 }) => ({
  gridColumn: `span ${span}`,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '0.375rem',
  '& .MuiOutlinedInput-root': {
    background: '#fff',
    '& fieldset':             { borderColor: hasError ? '#dc2626' : '#d1d5db' },
    '&:hover fieldset':       { borderColor: hasError ? '#dc2626' : '#3b82f6' },
    '&.Mui-focused fieldset': {
      borderColor: hasError ? '#dc2626' : '#3b82f6',
      boxShadow: hasError
        ? '0 0 0 3px rgba(220,38,38,0.12)'
        : '0 0 0 3px rgba(59,130,246,0.12)',
    },
  },
}));

const FieldLabel = styled(Typography)(() => ({
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: '#374151',
  textTransform: 'uppercase',
  letterSpacing: '0.025em',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.375rem',
}));

const RequiredMark = styled('span')(() => ({
  color: '#ef4444',
  marginLeft: '0.125rem',
}));

const DetailGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '1rem',
  [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' },
}));

const DetailItem = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
}));

const DetailLabel = styled(Typography)(() => ({
  fontSize: '0.6875rem',
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
}));

const DetailValue = styled(Typography)(() => ({
  fontSize: '0.9375rem',
  fontWeight: 600,
  color: '#111827',
}));

// ─────────────────────────────────────────────────────────────────────────────
// SX CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const helperErrorSx = {
  color: '#dc2626', fontSize: '0.75rem', fontWeight: 500,
  margin: 0, marginTop: '4px',
};

const helperSuccessSx = {
  color: '#059669', fontSize: '0.75rem', fontWeight: 500,
  mt: '4px', margin: 0,
};

const cancelBtnSx = {
  px: 3, py: 1,
  backgroundColor: '#f3f4f6', color: '#374151',
  border: '1px solid #d1d5db', borderRadius: '6px',
  fontWeight: 500, textTransform: 'none',
  '&:hover':    { backgroundColor: '#e5e7eb' },
  '&:disabled': { opacity: 0.6 },
};

const submitBtnSx = {
  px: 3, py: 1,
  backgroundColor: '#2563eb', color: '#fff',
  borderRadius: '6px', fontWeight: 600,
  textTransform: 'none', minWidth: '180px',
  display: 'flex', alignItems: 'center', gap: 1,
  '&:hover':    { backgroundColor: '#1d4ed8' },
  '&:disabled': { backgroundColor: '#93c5fd' },
};

// ─────────────────────────────────────────────────────────────────────────────
// DISPLAY HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const formatDate = (date) => {
  if (!date) return 'N/A';
  if (date instanceof Date)              return date.toLocaleDateString('en-GB');
  if (typeof date.toDate === 'function') return date.toDate().toLocaleDateString('en-GB');
  return String(date);
};

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '';
  return new Intl.NumberFormat('en-IN', {
    style:                 'currency',
    currency:              'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const ExtendWarrantyModal = ({ asset, onClose, onExtend, currentUser }) => {

  // ── Guard: disposed assets cannot be extended ─────────────────────────────
  const isBlocked = isWarrantyExtensionBlocked(asset);

  // ── Form state ────────────────────────────────────────────────────────────
  const [newExpiryDate,           setNewExpiryDate]           = useState('');
  const [extensionReason,         setExtensionReason]         = useState('');
  const [warrantyReferenceNumber, setWarrantyReferenceNumber] = useState('');
  const [warrantyCost,            setWarrantyCost]            = useState('');

  // ── UI state ──────────────────────────────────────────────────────────────
  const [loading,     setLoading]     = useState(false);
  const [errors,      setErrors]      = useState({});
  const [submitError, setSubmitError] = useState('');

  const clearError = (field) =>
    setErrors((prev) => (prev[field] ? { ...prev, [field]: '' } : prev));

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
  e.preventDefault();
  setSubmitError('');

  const safeReason = String(extensionReason ?? '').trim();
  const safeRefNo  = String(warrantyReferenceNumber ?? '').trim();
  const cost       = isNaN(parseFloat(warrantyCost)) ? 0 : parseFloat(warrantyCost);

  const newErrors = validateWarrantyExtensionForm({
    newExpiryDate,
    extensionReason:         safeReason,
    warrantyReferenceNumber: safeRefNo,
    warrantyCost:            cost,
    asset,
  });

  setErrors(newErrors);
  if (Object.keys(newErrors).length > 0) return;

  setLoading(true);
  try {
    const newExpiry = new Date(newExpiryDate);

    const payload = buildWarrantyExtensionPayload({
      asset,
      newExpiry,
      extensionReason:         safeReason,
      warrantyReferenceNumber: safeRefNo,
      warrantyCost:            cost,
      currentUser,
    });

    // Modal owns the write
    await assetService.upsertData(asset.id, payload);

    // Parent handles audit + refresh + toast + close
    if (onExtend) {
      await onExtend({
        assetId:                 asset.id,
        newExpiry,
        extensionReason:         safeReason,
        warrantyReferenceNumber: safeRefNo,
        warrantyCost:            cost,
      });
    }
  } catch (error) {
    console.error('Error extending warranty:', error);
    setSubmitError(error?.message || 'Failed to extend warranty. Please try again.');
  } finally {
    setLoading(false);
  }
};

  const showPreview =
    newExpiryDate && warrantyReferenceNumber && warrantyCost && !isBlocked;

  // ─────────────────────────────────────────────────────────────────────────
  // DISPOSED / BLOCKED VIEW
  // ─────────────────────────────────────────────────────────────────────────

  if (isBlocked) {
    return (
      <Dialog
        fullScreen open
        onClose={onClose}
        TransitionComponent={Transition}
        PaperProps={{
          sx: { display: 'flex', flexDirection: 'column', overflow: 'hidden' },
        }}
      >
        <AppBar sx={{ position: 'relative', flexShrink: 0 }} elevation={1}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={onClose} aria-label="Close">
              <CloseIcon />
            </IconButton>
            <Box sx={{ ml: 2, flex: 1 }}>
              <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                Warranty Extension Unavailable
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                This asset cannot have its warranty extended
              </Typography>
            </Box>
          </Toolbar>
        </AppBar>

        <Box sx={{ flex: 1, overflowY: 'auto', bgcolor: '#f1f5f9', py: 4 }}>
          <Container maxWidth="lg">
            <Stack spacing={3}>
              <Box sx={{
                backgroundColor: '#fef2f2', color: '#b91c1c',
                padding: '1rem', borderRadius: '8px',
                borderLeft: '4px solid #dc2626',
                display: 'flex', alignItems: 'flex-start', gap: 1.5,
              }}>
                <WarningAmberIcon sx={{ mt: 0.25, flexShrink: 0 }} />
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>
                    Asset Disposed
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', mt: 0.25 }}>
                    This asset has been disposed and warranty cannot be extended.
                    Disposed assets are no longer under warranty coverage.
                  </Typography>
                </Box>
              </Box>

              <SectionCard variant="outlined">
                <CardContent sx={{ p: 3 }}>
                  <SectionTitle>Asset Details</SectionTitle>
                  <DetailGrid>
                    <DetailItem>
                      <DetailLabel>Asset Name</DetailLabel>
                      <DetailValue>{asset?.assetName}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Asset ID</DetailLabel>
                      <DetailValue>{asset?.assetId}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Status</DetailLabel>
                      <DetailValue sx={{ color: '#dc2626' }}>{asset?.status}</DetailValue>
                    </DetailItem>
                    {asset?.disposalDate && (
                      <DetailItem>
                        <DetailLabel>Disposal Date</DetailLabel>
                        <DetailValue>
                          {asset.disposalDate?.toDate
                            ? asset.disposalDate.toDate().toLocaleDateString()
                            : asset.disposalDate}
                        </DetailValue>
                      </DetailItem>
                    )}
                  </DetailGrid>
                </CardContent>
              </SectionCard>
            </Stack>
          </Container>
        </Box>

        <Box sx={{
          borderTop: '1px solid #e2e8f0', backgroundColor: '#fff',
          py: 2, px: 3, boxShadow: '0 -2px 8px rgba(0,0,0,0.04)', flexShrink: 0,
        }}>
          <Container maxWidth={false} disableGutters>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={onClose} sx={cancelBtnSx}>Close</Button>
            </Box>
          </Container>
        </Box>
      </Dialog>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // MAIN FORM VIEW
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <Dialog
      fullScreen open
      onClose={!loading ? onClose : undefined}
      TransitionComponent={Transition}
      PaperProps={{
        sx: { display: 'flex', flexDirection: 'column', overflow: 'hidden' },
      }}
    >
      <AppBar sx={{ position: 'relative', flexShrink: 0 }} elevation={1}>
        <Toolbar>
          <IconButton
            edge="start" color="inherit"
            onClick={onClose} disabled={loading} aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ ml: 2, flex: 1 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
              Extend Warranty
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              Fill in the details below to extend the warranty for this asset
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{
        display: 'flex', flexDirection: 'column',
        bgcolor: '#f1f5f9', flex: 1, minHeight: 0, overflow: 'hidden',
      }}>
        <Box
          component="form"
          id="extend-warranty-form"
          onSubmit={handleSubmit}
          noValidate
          sx={{ flex: 1, overflowY: 'auto', py: 4 }}
        >
          <Container maxWidth="lg">
            <Stack spacing={3}>
              {submitError && (
                <Box sx={{
                  backgroundColor: '#fee2e2', color: '#b91c1c',
                  padding: '0.75rem 1rem', borderRadius: '6px',
                  fontSize: '0.875rem', borderLeft: '4px solid #dc2626',
                  display: 'flex', alignItems: 'center', gap: 1,
                }}>
                  <WarningAmberIcon fontSize="small" />
                  {submitError}
                </Box>
              )}

              <SectionCard variant="outlined">
                <CardContent sx={{ p: 3 }}>
                  <SectionTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InfoOutlinedIcon sx={{ fontSize: '1.125rem', color: '#3b82f6' }} />
                      Asset Details
                    </Box>
                  </SectionTitle>
                  <DetailGrid>
                    <DetailItem>
                      <DetailLabel>Asset Name</DetailLabel>
                      <DetailValue>{asset?.assetName}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Asset ID</DetailLabel>
                      <DetailValue>{asset?.assetId}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Current Warranty Expiry</DetailLabel>
                      <DetailValue sx={{ color: '#dc2626' }}>
                        {formatDate(asset?.warrantyExpiry)}
                      </DetailValue>
                    </DetailItem>
                  </DetailGrid>
                </CardContent>
              </SectionCard>

              <SectionCard variant="outlined">
                <CardContent sx={{ p: 3 }}>
                  <SectionTitle>Warranty Extension Details</SectionTitle>

                  <FormRow>
                    <FormGroup hasError={Boolean(errors.warrantyReferenceNumber)} span={4}>
                      <FieldLabel>
                        <ReceiptLongIcon sx={{ fontSize: '1rem', color: '#6b7280' }} />
                        Warranty Reference Number
                        <RequiredMark>*</RequiredMark>
                      </FieldLabel>
                      <TextField
                        id="warrantyReferenceNumber"
                        name="warrantyReferenceNumber"
                        value={warrantyReferenceNumber}
                        onChange={(e) => {
                          setWarrantyReferenceNumber(e.target.value);
                          clearError('warrantyReferenceNumber');
                        }}
                        placeholder="Enter warranty reference / invoice number"
                        fullWidth size="small"
                        error={Boolean(errors.warrantyReferenceNumber)}
                        helperText={errors.warrantyReferenceNumber}
                        FormHelperTextProps={{ sx: helperErrorSx }}
                      />
                    </FormGroup>
                  </FormRow>

                  <FormRow>
                    <FormGroup hasError={Boolean(errors.newExpiryDate)} span={2}>
                      <FieldLabel>
                        <CalendarMonthIcon sx={{ fontSize: '1rem', color: '#6b7280' }} />
                        New Warranty Expiry Date
                        <RequiredMark>*</RequiredMark>
                      </FieldLabel>
                      <TextField
                        type="date"
                        id="newExpiryDate"
                        name="newExpiryDate"
                        value={newExpiryDate}
                        onChange={(e) => {
                          setNewExpiryDate(e.target.value);
                          clearError('newExpiryDate');
                        }}
                        inputProps={{ min: getWarrantyMinDate(asset) }}
                        fullWidth size="small"
                        error={Boolean(errors.newExpiryDate)}
                        helperText={errors.newExpiryDate}
                        FormHelperTextProps={{ sx: helperErrorSx }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </FormGroup>

                    <FormGroup hasError={Boolean(errors.warrantyCost)} span={2}>
                      <FieldLabel>
                        <CurrencyRupeeIcon sx={{ fontSize: '1rem', color: '#6b7280' }} />
                        Cost of Warranty (₹)
                        <RequiredMark>*</RequiredMark>
                      </FieldLabel>
                      <TextField
                        type="number"
                        id="warrantyCost"
                        name="warrantyCost"
                        value={warrantyCost}
                        onChange={(e) => {
                          setWarrantyCost(e.target.value);
                          clearError('warrantyCost');
                        }}
                        placeholder="0.00"
                        inputProps={{ min: 0, step: '0.01' }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
                                ₹
                              </Typography>
                            </InputAdornment>
                          ),
                        }}
                        fullWidth size="small"
                        error={Boolean(errors.warrantyCost)}
                        helperText={
                          errors.warrantyCost ||
                          (warrantyCost && !errors.warrantyCost
                            ? `Amount: ${formatCurrency(parseFloat(warrantyCost))}`
                            : '')
                        }
                        FormHelperTextProps={{
                          sx: errors.warrantyCost ? helperErrorSx : helperSuccessSx,
                        }}
                      />
                    </FormGroup>
                  </FormRow>

                  <FormRow>
                    <FormGroup hasError={Boolean(errors.extensionReason)} span={4}>
                      <FieldLabel>
                        <ChatIcon sx={{ fontSize: '1rem', color: '#6b7280' }} />
                        Reason for Extension
                        <RequiredMark>*</RequiredMark>
                      </FieldLabel>
                      <TextField
                        id="extensionReason"
                        name="extensionReason"
                        value={extensionReason}
                        onChange={(e) => {
                          setExtensionReason(e.target.value);
                          clearError('extensionReason');
                        }}
                        placeholder="Please provide a detailed reason for the warranty extension (e.g., Vendor warranty renewal, Extended support contract, etc.)"
                        multiline minRows={3} maxRows={6}
                        fullWidth size="small"
                        error={Boolean(errors.extensionReason)}
                        helperText={errors.extensionReason}
                        FormHelperTextProps={{ sx: helperErrorSx }}
                      />
                    </FormGroup>
                  </FormRow>
                </CardContent>
              </SectionCard>

              {showPreview && (
                <SectionCard variant="outlined">
                  <CardContent sx={{ p: 3 }}>
                    <SectionTitle>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarTodayIcon sx={{ fontSize: '1.125rem', color: '#059669' }} />
                        Extension Summary
                      </Box>
                    </SectionTitle>

                    <DetailGrid>
                      <DetailItem>
                        <DetailLabel>Reference Number</DetailLabel>
                        <DetailValue sx={{ color: '#1d4ed8' }}>
                          {warrantyReferenceNumber}
                        </DetailValue>
                      </DetailItem>

                      <DetailItem>
                        <DetailLabel>Extension Period</DetailLabel>
                        <Box sx={{
                          display: 'flex', alignItems: 'center',
                          gap: 0.75, flexWrap: 'wrap',
                        }}>
                          <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: '#dc2626' }}>
                            {formatDate(asset?.warrantyExpiry)}
                          </Typography>
                          <ArrowForwardIcon sx={{ fontSize: '1rem', color: '#6b7280' }} />
                          <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: '#059669' }}>
                            {formatDate(new Date(newExpiryDate))}
                          </Typography>
                        </Box>
                      </DetailItem>

                      <DetailItem>
                        <DetailLabel>Extension Cost</DetailLabel>
                        <DetailValue sx={{ color: '#059669' }}>
                          {formatCurrency(parseFloat(warrantyCost))}
                        </DetailValue>
                      </DetailItem>
                    </DetailGrid>
                  </CardContent>
                </SectionCard>
              )}
            </Stack>
          </Container>
        </Box>

        <Box sx={{
          borderTop: '1px solid #e2e8f0', backgroundColor: '#fff',
          py: 2, px: 3, boxShadow: '0 -2px 8px rgba(0,0,0,0.04)', flexShrink: 0,
        }}>
          <Container maxWidth={false} disableGutters>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
              <Button type="button" onClick={onClose} disabled={loading} sx={cancelBtnSx}>
                Cancel
              </Button>
              <Button
                type="submit"
                form="extend-warranty-form"
                disabled={loading}
                sx={submitBtnSx}
              >
                {loading && <CircularProgress size={16} color="inherit" />}
                {loading ? 'Extending Warranty…' : 'Extend Warranty'}
              </Button>
            </Box>
          </Container>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ExtendWarrantyModal;